import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { ChartRenderer, ChartDescriptionModal } from '../components/ChartRenderer';
import { useAnalyticsPackage } from '../context/AnalyticsPackageContext';

const ACTIVE_BG   = '#958945';
const ACTIVE_TEXT = '#ffffff';
const LOCKED_BG   = '#f0f0f0';
const LOCKED_TEXT = '#999999';

interface Market {
  id: number;
  name: string;
  hasAccess: boolean;
  defaultPropertyType: string | null;
  photoUrl: string | null;
}

interface Neighborhood {
  id: number;
  name: string;
  hasAccess: boolean;
  photoUrl: string | null;
}

interface BuildingDetailDto {
  buildingId: number;
  name: string | null;
  buildingCategory: string | null;
  neighborhoodId: number | null;
  neighborhood: string | null;
  address: string | null;
  zip: string | null;
  status: number | null;
  occupancyDate: string | null;
  floors: number | null;
  units: number | null;
  waterfront: boolean;
  concierge: boolean;
  parking: boolean;
  resales: number | null;
  earliestSale: string | null;
  mostRecentSale: string | null;
  annualizedAppreciation: number | null;
  unitsSold: number | null;
  pctSoldOut: number | null;
  avgPrice: number | null;
  avgPsf: number | null;
  photoUrl: string | null;
}

interface OrgInfo {
  id: number;
  name: string;
  logoUrl: string | null;
}

interface ChartOption {
  id: number;
  name: string;
  title: string;
  description: string | null;
}

interface ChartData {
  categories: string[];
  years?: number[];
  series: { name: string; data: (number | null)[] }[];
}

function fmtPrice(v: number | null): string {
  if (v == null) return '—';
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function BuildingLandingPage() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const { authFetch } = useApi();
  const { addItem } = useAnalyticsPackage();

  const [markets, setMarkets] = useState<Market[]>([]);
  const [neighborhoodsByMarket, setNeighborhoodsByMarket] = useState<Record<number, Neighborhood[]>>({});
  const [expandedMarkets, setExpandedMarkets] = useState<number[]>([]);
  const [building, setBuilding] = useState<BuildingDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [buildingCharts, setBuildingCharts] = useState<ChartOption[]>([]);
  const [selectedChartName, setSelectedChartName] = useState<string | null>(null);
  const [showChartDescription, setShowChartDescription] = useState(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);

  const owningMarketIdRef = useRef<number | null>(null);

  useEffect(() => {
    authFetch('/api/me')
      .then(r => r.json())
      .then((data: any) => { if (data.organization) setOrgInfo(data.organization); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    authFetch('/api/markets')
      .then(r => r.json())
      .then(async (mktData: Market[]) => {
        setMarkets(mktData);
        const accessible = mktData.filter(m => m.hasAccess);
        const entries = await Promise.all(
          accessible.map(m =>
            authFetch(`/api/markets/${m.id}/neighborhoods`)
              .then(r => r.json())
              .then((nhs: Neighborhood[]) => [m.id, nhs] as [number, Neighborhood[]])
          )
        );
        const nhMap: Record<number, Neighborhood[]> = {};
        entries.forEach(([mid, nhs]) => { nhMap[mid] = nhs; });
        setNeighborhoodsByMarket(nhMap);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    const controller = new AbortController();
    authFetch(`/api/buildings/${id}`, { signal: controller.signal })
      .then(r => {
        if (r.status === 404 || r.status === 403) { setNotFound(true); setLoading(false); return null; }
        return r.json();
      })
      .then((data: BuildingDetailDto | null) => {
        if (!data) return;
        setBuilding(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') { setNotFound(true); setLoading(false); }
      });
    return () => controller.abort();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    authFetch(`/api/buildings/${id}/charts`)
      .then(r => r.json())
      .then((data: ChartOption[]) => {
        setBuildingCharts(data);
        if (data.length > 0) setSelectedChartName(data[0].name);
      })
      .catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedChartName || !id) return;
    const urls: Record<string, string> = {
      monthlyClosings:        `/api/charts/monthly-closings?buildingId=${id}`,
      salesPrices:            `/api/charts/sales-prices?buildingId=${id}&propertyType=C`,
      salesPSF:               `/api/charts/sales-psf?buildingId=${id}&propertyType=C`,
      salesPriceDistribution: `/api/charts/price-range-trends?buildingId=${id}&propertyType=C`,
    };
    const url = urls[selectedChartName];
    if (!url) return;
    setChartLoading(true);
    setChartData(null);
    const controller = new AbortController();
    authFetch(url, { signal: controller.signal })
      .then(r => r.json())
      .then((data: ChartData) => { setChartData(data); setChartLoading(false); })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') setChartLoading(false);
      });
    return () => controller.abort();
  }, [selectedChartName, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!building?.neighborhoodId) return;
    const key = Object.keys(neighborhoodsByMarket).find(mid =>
      neighborhoodsByMarket[Number(mid)].some(n => n.id === building.neighborhoodId)
    );
    if (key) {
      owningMarketIdRef.current = Number(key);
      setExpandedMarkets([Number(key)]);
    }
  }, [building, neighborhoodsByMarket]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarketToggle = (e: React.MouseEvent, marketId: number) => {
    e.stopPropagation();
    setExpandedMarkets(prev =>
      prev.includes(marketId) ? prev.filter(x => x !== marketId) : [...prev, marketId]
    );
  };

  const neighborhoodPhotoUrl = building?.neighborhoodId
    ? `https://prismassets.blob.core.windows.net/neighborhood-images/${building.neighborhoodId}.jpg`
    : null;

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 61px)' }}>

      {/* Left accordion */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: '1px solid #ddd',
        padding: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 0,
        background: '#f6f5f1', borderTop: '1px solid #ddd',
      }}>
        <div style={{ padding: '1rem', fontFamily: "'Montserrat', sans-serif", fontSize: 9, fontWeight: 700, color: '#958945', letterSpacing: 3 }}>ACTIVE MARKETS</div>
        {(() => {
          const activeMarketId = owningMarketIdRef.current ?? -1;
          return markets
            .filter(m => m.hasAccess)
            .sort((a, b) => a.id === activeMarketId ? -1 : b.id === activeMarketId ? 1 : 0)
            .map(market => {
              const isExpanded = expandedMarkets.includes(market.id);
              const neighborhoods = neighborhoodsByMarket[market.id] ?? [];

              return (
                <React.Fragment key={market.id}>
                  <div
                    onClick={() => { setExpandedMarkets([market.id]); navigate(`/market/${market.id}`); }}
                    style={{
                      padding: '0.75rem',
                      cursor: 'pointer',
                      backgroundColor: '#f6f5f1',
                      color: '#403B36',
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 400, fontSize: 15,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      userSelect: 'none',
                      borderBottom: '1px solid #ddd',
                    }}
                  >
                    <span>{market.name}</span>
                    <span
                      onClick={e => handleMarketToggle(e, market.id)}
                      style={{ fontSize: 10, marginLeft: 6, padding: '0 0.25rem', cursor: 'pointer' }}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </div>

                  {isExpanded && neighborhoods.map(n => {
                    const isActive = n.id === building?.neighborhoodId;
                    return (
                      <div
                        key={n.id}
                        onClick={() => { if (n.hasAccess) navigate(`/neighborhood/${n.id}`); }}
                        title={n.hasAccess ? `Click to view ${n.name} insights` : `Click to add ${n.name} to your subscription`}
                        style={{
                          padding: '0.75rem 0.75rem 0.75rem 1.5rem',
                          cursor: n.hasAccess ? 'pointer' : 'default',
                          background: isActive ? ACTIVE_BG : !n.hasAccess ? LOCKED_BG : '#f6f5f1',
                          color: isActive ? ACTIVE_TEXT : !n.hasAccess ? LOCKED_TEXT : '#403B36',
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: isActive ? 'bold' : 400,
                          fontSize: 13,
                          borderBottom: '1px solid #ddd',
                          boxShadow: isActive ? 'inset 3px 0 0 #bfb261' : 'none',
                        }}
                      >
                        {n.name}
                      </div>
                    );
                  })}

                  {isExpanded && neighborhoods.length === 0 && (
                    <div style={{ padding: '0.45rem 0.75rem', color: '#aaa', fontSize: 13, fontFamily: "'Montserrat', sans-serif", borderBottom: '1px solid #ddd' }}>
                      Loading...
                    </div>
                  )}
                </React.Fragment>
              );
            });
        })()}
        <div style={{ marginTop: 'auto', padding: '1rem' }}>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 8, fontWeight: 400, color: '#8C877D', letterSpacing: 2.5 }}>POWERED BY</div>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 700, color: '#736E66', letterSpacing: 2 }}>LINK ANALYTICS</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0, background: '#ffffff', display: 'flex', flexDirection: 'column' }}>

        {/* Hero */}
        <div style={{
          height: 184,
          backgroundImage: neighborhoodPhotoUrl
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${neighborhoodPhotoUrl})`
            : undefined,
          backgroundColor: '#ccc',
          backgroundSize: 'cover', backgroundPosition: 'center',
          display: 'flex', alignItems: 'flex-end',
          padding: '0 2rem 0.25rem', flexShrink: 0, overflow: 'hidden',
        }}>
          <span style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '64pt',
            fontWeight: 'bold',
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 0.6,
            letterSpacing: '-1px',
          }}>
            {building?.neighborhood ?? ''}
          </span>
        </div>

        <div style={{ padding: '2rem' }}>

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                border: '4px solid #e0dbd7', borderTopColor: '#ba9394',
                animation: 'prism-spin 0.75s linear infinite',
              }} />
            </div>
          )}

          {notFound && (
            <div style={{ fontFamily: "'Montserrat', sans-serif", color: '#999', fontSize: 16 }}>
              Building not found.
            </div>
          )}

          {!loading && building && (
            <>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>

              {/* Sidekick portrait */}
              <div style={{
                width: 200, height: 250, flexShrink: 0,
                backgroundImage: building.photoUrl ? `url(${building.photoUrl})` : undefined,
                backgroundColor: '#ccc',
                backgroundSize: 'cover', backgroundPosition: 'center',
              }} />

              {/* Details */}
              <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <div style={{ fontSize: '30pt', fontWeight: 500, color: '#000', lineHeight: 1.1 }}>
                  {building.name ?? '—'}
                </div>
                {(building.address || building.zip || building.neighborhood) && (
                  <div style={{ fontSize: 22, color: '#333', marginTop: '0.4rem', display: 'flex', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    {(building.address || building.zip) && (
                      <span>{[building.address, building.zip].filter(Boolean).join(', ')}</span>
                    )}
                    {building.neighborhoodId && building.neighborhood && (
                      <span
                        onClick={() => navigate(`/neighborhood/${building.neighborhoodId}`)}
                        style={{ color: '#ba9394', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {building.neighborhood}
                      </span>
                    )}
                  </div>
                )}
                {building.buildingCategory && (
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#66635F', marginTop: '0.3rem' }}>
                    {building.buildingCategory}
                  </div>
                )}

                <div style={{ fontSize: 14, color: '#333', marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {building.earliestSale && (
                    <div>Building opened: {fmtDate(building.earliestSale)}</div>
                  )}
                  {building.pctSoldOut != null && (
                    <div>
                      {(building.pctSoldOut * 100).toFixed(1)}% of units sold
                      {building.unitsSold != null && building.units != null
                        ? ` (${building.unitsSold} of ${building.units})`
                        : ''}
                    </div>
                  )}
                  {building.avgPrice != null && (
                    <div>Average sale price: {fmtPrice(building.avgPrice)}</div>
                  )}
                  {building.avgPsf != null && (
                    <div>Average $/SqFt: {fmtPrice(building.avgPsf)}</div>
                  )}
                  {building.annualizedAppreciation != null && (
                    <div style={{ color: building.annualizedAppreciation >= 0 ? 'rgb(3,123,102)' : 'rgb(214,10,34)' }}>
                      {(building.annualizedAppreciation * 100).toFixed(1)}% annual appreciation ({building.resales} resales)
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Charts */}
            {buildingCharts.length > 0 && (
              <div style={{ marginTop: '2.5rem' }}>
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '22pt', fontWeight: 500, color: '#000' }}>
                    Charts
                  </div>
                  <select
                    value={selectedChartName ?? ''}
                    onChange={e => { setSelectedChartName(e.target.value || null); }}
                    style={{ padding: '0.5rem', fontSize: 16 }}
                  >
                    {buildingCharts.map(c => (
                      <option key={c.id} value={c.name}>{c.title}</option>
                    ))}
                  </select>

                  {(() => {
                    const selected = buildingCharts.find(c => c.name === selectedChartName);
                    if (!selected?.description) return null;
                    return (
                      <button
                        onClick={() => setShowChartDescription(true)}
                        aria-label={`About ${selected.title}`}
                        title={`About ${selected.title}`}
                        style={{
                          width: 22, height: 22, padding: 0, cursor: 'pointer',
                          border: '1px solid #ccc', borderRadius: '50%', background: '#fff',
                          color: '#888', fontSize: 12, fontStyle: 'italic', fontWeight: 700,
                          lineHeight: '20px', textAlign: 'center',
                        }}
                      >
                        i
                      </button>
                    );
                  })()}
                </div>

                {chartLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%',
                      border: '4px solid #e0dbd7', borderTopColor: '#ba9394',
                      animation: 'prism-spin 0.75s linear infinite',
                    }} />
                  </div>
                )}

                {!chartLoading && chartData && selectedChartName && (() => {
                  const selected = buildingCharts.find(c => c.name === selectedChartName);
                  if (!selected) return null;
                  return (
                    <ChartRenderer
                      chartName={selected.name}
                      chartTitle={selected.title}
                      neighborhoodId={0}
                      neighborhoodName={building.name ?? ''}
                      propertyTypeLabel="Condominium"
                      data={chartData}
                      orgLogoUrl={orgInfo?.logoUrl ? `${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${orgInfo.id}/logo-bytes` : undefined}
                      onAddToPackage={(hiddenSeries) => {
                        addItem({
                          entityType: 'building',
                          entityId: id,
                          entityName: building.name ?? '',
                          chartName: selected.name,
                          chartTitle: selected.title,
                          propertyType: null,
                          buildingCategoryId: null,
                          yearStart: null,
                          yearEnd: null,
                          subareaId: null,
                          subareaName: null,
                          sortOrder: null,
                          hiddenSeries: hiddenSeries.length ? hiddenSeries : undefined,
                        });
                      }}
                    />
                  );
                })()}

                {showChartDescription && (() => {
                  const selected = buildingCharts.find(c => c.name === selectedChartName);
                  if (!selected?.description) return null;
                  return (
                    <ChartDescriptionModal
                      title={selected.title}
                      description={selected.description}
                      onClose={() => setShowChartDescription(false)}
                    />
                  );
                })()}
              </div>
            )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
